/**
 * Sale Countdown Module
 *
 * On shop pages, shows a live countdown driven by the discount schedule
 * (data/discounts.toml → window.VANYX_CONFIG.discountSchedule):
 *   - before the sale: counts down to the start ("<name> startet in …")
 *   - during the sale: counts down the time left ("<name> endet in …")
 *   - after the sale:  hides itself
 *
 * The sale name comes from the config; the phase verb is localised. Needs a
 * `[data-sale-countdown]` container, so it is a no-op on pages without one.
 */

import { getSchedule } from "./discount-config.js";

const STRINGS = {
  de: { before: "startet in", during: "endet in", d: "Tage", h: "Std", m: "Min", s: "Sek" },
  en: { before: "starts in", during: "ends in", d: "days", h: "h", m: "min", s: "s" },
};

const DEFAULT_NAME = "Sale";

function pad(n) {
  return String(n).padStart(2, "0");
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c],
  );
}

function breakdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    mins: Math.floor((total % 3600) / 60),
    secs: total % 60,
  };
}

function render(el, name, verb, b, running, t) {
  const seg = (value, unit) =>
    `<span class="sale-countdown__seg"><span class="sale-countdown__num">${value}</span>` +
    `<span class="sale-countdown__unit">${unit}</span></span>`;

  const time =
    (b.days > 0 ? seg(b.days, t.d) : "") +
    seg(pad(b.hours), t.h) +
    seg(pad(b.mins), t.m) +
    seg(pad(b.secs), t.s);

  el.className = "sale-countdown" + (running ? " sale-countdown--running" : "");
  el.innerHTML =
    `<span class="sale-countdown__intro">` +
    `<span class="sale-countdown__name">${escapeHtml(name)}</span> ` +
    `<span class="sale-countdown__label">${verb}</span>` +
    `</span>` +
    `<span class="sale-countdown__time">${time}</span>`;
}

export function init() {
  const el = document.querySelector("[data-sale-countdown]");
  if (!el) {
    return;
  }

  const schedule = getSchedule();
  const start = schedule.startsAt ? Date.parse(schedule.startsAt) : NaN;
  const end = schedule.endsAt ? Date.parse(schedule.endsAt) : NaN;
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return;
  }

  const lang = (window.VANYX_CONFIG || {}).lang === "en" ? "en" : "de";
  const t = STRINGS[lang];
  const name = schedule.name || DEFAULT_NAME;

  let timer = null;
  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  function tick() {
    const now = Date.now();
    if (now < start) {
      render(el, name, t.before, breakdown(start - now), false, t);
      el.hidden = false;
    } else if (now < end) {
      render(el, name, t.during, breakdown(end - now), true, t);
      el.hidden = false;
    } else {
      el.hidden = true;
      stop();
    }
  }

  tick();
  if (Date.now() < end) {
    timer = setInterval(tick, 1000);
  }
}
